import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRightLeft, Save } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { LoadingState } from '../components/ui/loading-state';
import { PageHeader } from '../components/ui/page-header';
import { QueueSettingDto, TenantDto } from '../models';
import { getApiError, getQueueSettings, getTenants, updateQueueSettings } from '../services/api';

export const QueueSettingsPage = () => {
  const [tenants, setTenants] = useState<TenantDto[]>();
  const [settings, setSettings] = useState<QueueSettingDto[]>();
  const [saving, setSaving] = useState<number>();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const [tenantResponse, settingsResponse] = await Promise.all([getTenants(), getQueueSettings()]);
      setTenants(tenantResponse.data.data);
      const existing = settingsResponse.data.data;
      setSettings(tenantResponse.data.data.map((tenant) => existing.find((x) => x.tenantId === tenant.id) || {
        tenantId: tenant.id,
        enabled: false,
        offerTimeoutSeconds: 30,
        primaryOrganizationSlaSeconds: 600,
        fallbackTenantId: null,
      }));
    } catch (e) {
      setError(getApiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const tenantNames = useMemo(() => new Map((tenants || []).map((x) => [x.id, x.name])), [tenants]);

  const patch = (tenantId: number, values: Partial<QueueSettingDto>) => {
    setSettings((current) => current?.map((item) => item.tenantId === tenantId ? { ...item, ...values } : item));
  };

  const save = async (setting: QueueSettingDto) => {
    setSaving(setting.tenantId);
    setError('');
    setSuccess('');
    try {
      await updateQueueSettings(setting.tenantId, {
        enabled: setting.enabled,
        offerTimeoutSeconds: Number(setting.offerTimeoutSeconds),
        primaryOrganizationSlaSeconds: Number(setting.primaryOrganizationSlaSeconds),
        fallbackTenantId: setting.fallbackTenantId || null,
      });
      setSuccess(`Настройки очереди «${tenantNames.get(setting.tenantId)}» сохранены`);
    } catch (e) {
      setError(getApiError(e));
    } finally {
      setSaving(undefined);
    }
  };

  return (
    <AppLayout title="Маршрутизация">
      <PageHeader title="Маршрутизация очереди" description="Основная очередь работает всегда. Переключатель управляет только перебросом в резервную медицинскую организацию." />
      {error && <div className="mb-4 rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>}
      {success && <div className="mb-4 rounded-xl border border-success/20 bg-success/10 px-4 py-3 text-sm text-success">{success}</div>}

      {!settings || !tenants ? <LoadingState /> : (
        <div className="grid gap-4 xl:grid-cols-2">
          {settings.filter((setting) => setting.tenantId !== 1).map((setting) => (
            <section key={setting.tenantId} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-accent text-primary"><ArrowRightLeft className="h-5 w-5" /></div>
                  <div><h2 className="font-bold">{tenantNames.get(setting.tenantId)}</h2><p className="text-xs text-muted-foreground">Tenant #{setting.tenantId}</p></div>
                </div>
                <label className="flex cursor-pointer items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-2 text-xs font-semibold">
                  <input type="checkbox" checked={setting.enabled} onChange={(e) => patch(setting.tenantId, { enabled: e.target.checked })} className="h-4 w-4 accent-primary" />
                  Переброс {setting.enabled ? 'включён' : 'выключен'}
                </label>
              </div>

              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className="space-y-1.5 text-sm font-medium">
                  <span>Резервная организация</span>
                  <select value={setting.fallbackTenantId || ''} onChange={(e) => patch(setting.tenantId, { fallbackTenantId: e.target.value ? Number(e.target.value) : null })} className="h-10 w-full rounded-lg border border-border bg-background px-3 text-sm outline-none focus:border-primary">
                    <option value="">Не выбрана</option>
                    {tenants.filter((tenant) => tenant.id !== setting.tenantId && tenant.id !== 1).map((tenant) => <option key={tenant.id} value={tenant.id}>{tenant.name}</option>)}
                  </select>
                </label>
                <label className="space-y-1.5 text-sm font-medium">
                  <span>Ожидание основной организации, сек.</span>
                  <input type="number" min={10} max={86400} value={setting.primaryOrganizationSlaSeconds} onChange={(e) => patch(setting.tenantId, { primaryOrganizationSlaSeconds: Number(e.target.value) })} className="h-10 w-full rounded-lg border border-border bg-background px-3 outline-none focus:border-primary" />
                </label>
                <label className="space-y-1.5 text-sm font-medium">
                  <span>Время предложения медику, сек.</span>
                  <input type="number" min={5} max={3600} value={setting.offerTimeoutSeconds} onChange={(e) => patch(setting.tenantId, { offerTimeoutSeconds: Number(e.target.value) })} className="h-10 w-full rounded-lg border border-border bg-background px-3 outline-none focus:border-primary" />
                </label>
                <div className="flex items-end"><Button className="w-full" disabled={saving === setting.tenantId} onClick={() => save(setting)}><Save className="h-4 w-4" />{saving === setting.tenantId ? 'Сохранение...' : 'Сохранить'}</Button></div>
              </div>
            </section>
          ))}
        </div>
      )}
    </AppLayout>
  );
};
